// src/services/currencyService.js
// Centralized currency management service with real-time exchange rates

// Exchange rate cache (stores rates for 1 hour)
let exchangeRateCache = {
  rates: null,
  timestamp: null,
  cacheDuration: 60 * 60 * 1000 // 1 hour in milliseconds
};

const CurrencyService = {
  // Supported currencies with expanded list
  SUPPORTED_CURRENCIES: [
    // Major Global Currencies
    { code: 'USD', name: 'US Dollar', symbol: '$', flag: '🇺🇸', region: 'Americas', isActive: true },
    { code: 'EUR', name: 'Euro', symbol: '€', flag: '🇪🇺', region: 'Europe', isActive: true },
    { code: 'GBP', name: 'British Pound', symbol: '£', flag: '🇬🇧', region: 'Europe', isActive: true },
    
    // Asia-Pacific
    { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', flag: '🇸🇬', region: 'Asia-Pacific', isActive: true },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', flag: '🇦🇺', region: 'Asia-Pacific', isActive: true },
    { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', flag: '🇳🇿', region: 'Asia-Pacific', isActive: true },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥', flag: '🇯🇵', region: 'Asia-Pacific', isActive: true },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', flag: '🇨🇳', region: 'Asia-Pacific', isActive: true },
    { code: 'KRW', name: 'Korean Won', symbol: '₩', flag: '🇰🇷', region: 'Asia-Pacific', isActive: true },
    { code: 'VND', name: 'Vietnamese Dong', symbol: '₫', flag: '🇻🇳', region: 'Asia-Pacific', isActive: true },
    
    // Europe
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', flag: '🇨🇭', region: 'Europe', isActive: true },
    
    // Americas
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', flag: '🇨🇦', region: 'Americas', isActive: true },
    { code: 'MXN', name: 'Mexican Peso', symbol: '$', flag: '🇲🇽', region: 'Americas', isActive: false },
    { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', flag: '🇧🇷', region: 'Americas', isActive: false },
    
    // Middle East & Africa
    { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', flag: '🇦🇪', region: 'Middle East', isActive: false },
    { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼', flag: '🇸🇦', region: 'Middle East', isActive: false },
    { code: 'ZAR', name: 'South African Rand', symbol: 'R', flag: '🇿🇦', region: 'Africa', isActive: false }
  ],

  // Fallback static exchange rates (base: USD)
  // Used when API is unavailable or as initial values
  FALLBACK_EXCHANGE_RATES: {
    'USD': 1.0,
    'EUR': 0.85,
    'GBP': 0.73,
    'SGD': 1.35,
    'AUD': 1.45,
    'NZD': 1.55,
    'JPY': 110.0,
    'CNY': 6.45,
    'KRW': 1180.0,
    'VND': 23000.0,
    'CHF': 0.92,
    'CAD': 1.25,
    'MXN': 20.0,
    'BRL': 5.2,
    'AED': 3.67,
    'SAR': 3.75,
    'ZAR': 14.5
  },

  // Get current exchange rates (from cache or API)
  async getExchangeRates() {
    // Check if cache is valid
    if (exchangeRateCache.rates && exchangeRateCache.timestamp) {
      const cacheAge = Date.now() - exchangeRateCache.timestamp;
      if (cacheAge < exchangeRateCache.cacheDuration) {
        return exchangeRateCache.rates;
      }
    }

    // Fetch fresh rates from API
    try {
      const rates = await this.fetchExchangeRatesFromAPI();
      if (rates) {
        exchangeRateCache.rates = rates;
        exchangeRateCache.timestamp = Date.now();
        return rates;
      }
    } catch (error) {
      console.warn('Failed to fetch exchange rates from API, using cached or fallback rates:', error);
    }

    // Return cached rates if available, otherwise fallback
    return exchangeRateCache.rates || this.FALLBACK_EXCHANGE_RATES;
  },

  // Fetch exchange rates from free API (ExchangeRate-API.com)
  // Primary API: https://open.er-api.com/v6/latest/USD (no API key required)
  // Fallback API: https://api.exchangerate-api.com/v4/latest/USD (no API key required)
  // Both APIs provide free tier access with hourly updates
  async fetchExchangeRatesFromAPI() {
    try {
      // Primary: ExchangeRate-API.com (open.er-api.com)
      // Free tier: No API key required, hourly updates
      const response = await fetch('https://open.er-api.com/v6/latest/USD');
      
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data && data.rates) {
        // Convert rates to our format (base: USD)
        const rates = { 'USD': 1.0 };
        Object.keys(data.rates).forEach(currency => {
          rates[currency] = data.rates[currency];
        });
        return rates;
      }
      
      throw new Error('Invalid API response format');
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
      // Try alternative free API
      try {
        const altResponse = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
        if (altResponse.ok) {
          const altData = await altResponse.json();
          if (altData && altData.rates) {
            const rates = { 'USD': 1.0, ...altData.rates };
            return rates;
          }
        }
      } catch (altError) {
        console.error('Alternative API also failed:', altError);
      }
      return null;
    }
  },

  // Initialize exchange rates on service load
  async initializeExchangeRates() {
    try {
      await this.getExchangeRates();
    } catch (error) {
      console.warn('Failed to initialize exchange rates:', error);
    }
  },

  // Get all currencies
  getAllCurrencies() {
    return this.SUPPORTED_CURRENCIES;
  },

  // Get active currencies only
  getActiveCurrencies() {
    return this.SUPPORTED_CURRENCIES.filter(currency => currency.isActive);
  },

  // Get currency by code
  getCurrencyByCode(code) {
    return this.SUPPORTED_CURRENCIES.find(currency => currency.code === code);
  },

  // Get currencies by region
  getCurrenciesByRegion(region) {
    return this.SUPPORTED_CURRENCIES.filter(currency => currency.region === region);
  },

  // Get unique regions
  getRegions() {
    return [...new Set(this.SUPPORTED_CURRENCIES.map(currency => currency.region))];
  },

  // Format currency amount
  formatAmount(amount, currency = 'USD', locale = 'en-US') {
    if (amount === null || amount === undefined || isNaN(amount)) return '';
    
    try {
      const currencyData = this.getCurrencyByCode(currency);
      const symbol = currencyData ? currencyData.symbol : currency;
      
      // For currencies with custom symbols, use manual formatting
      if (currencyData && ['CHF', 'AED', 'SAR'].includes(currency)) {
        return `${symbol} ${parseFloat(amount).toLocaleString(locale, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })}`;
      }
      
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    } catch (error) {
      console.error('Error formatting currency:', error);
      return `${amount}`;
    }
  },

  // Get currency symbol
  getCurrencySymbol(currency) {
    const currencyData = this.getCurrencyByCode(currency);
    return currencyData ? currencyData.symbol : currency;
  },

  // Get currency flag
  getCurrencyFlag(currency) {
    const currencyData = this.getCurrencyByCode(currency);
    return currencyData ? currencyData.flag : '';
  },

  // Validate currency code
  isValidCurrency(code) {
    return this.SUPPORTED_CURRENCIES.some(currency => currency.code === code);
  },

  // Convert amount between currencies (requires exchange rate)
  convertAmountWithRate(amount, fromCurrency, toCurrency, exchangeRate) {
    if (fromCurrency === toCurrency) return amount;
    if (!exchangeRate || exchangeRate <= 0) {
      console.warn('Invalid exchange rate provided');
      return amount;
    }
    return amount * exchangeRate;
  },

  // Parse currency amount from string
  parseAmount(amountString, currency = 'USD') {
    if (!amountString) return 0;
    
    // Remove currency symbols and commas
    const cleanAmount = amountString
      .replace(/[^\d.-]/g, '')
      .replace(/,/g, '');
    
    const parsed = parseFloat(cleanAmount);
    return isNaN(parsed) ? 0 : parsed;
  },

  // Get currency options for select components
  getCurrencyOptions(includeInactive = false) {
    const currencies = includeInactive ? this.getAllCurrencies() : this.getActiveCurrencies();
    
    return currencies.map(currency => ({
      value: currency.code,
      label: `${currency.flag} ${currency.code} - ${currency.name}`,
      symbol: currency.symbol,
      flag: currency.flag,
      region: currency.region,
      isActive: currency.isActive
    }));
  },

  // Get currency options grouped by region
  getCurrencyOptionsByRegion(includeInactive = false) {
    const currencies = includeInactive ? this.getAllCurrencies() : this.getActiveCurrencies();
    const grouped = {};
    
    currencies.forEach(currency => {
      if (!grouped[currency.region]) {
        grouped[currency.region] = [];
      }
      grouped[currency.region].push({
        value: currency.code,
        label: `${currency.flag} ${currency.code} - ${currency.name}`,
        symbol: currency.symbol,
        flag: currency.flag
      });
    });
    
    return grouped;
  },

  // Get common investment currencies (most frequently used)
  getCommonInvestmentCurrencies() {
    return ['USD', 'EUR', 'GBP', 'SGD', 'AUD', 'CAD', 'JPY', 'CHF']
      .map(code => this.getCurrencyByCode(code))
      .filter(Boolean);
  },

  // Get region-based default currencies
  getDefaultCurrencyByRegion(region) {
    const regionDefaults = {
      'Americas': 'USD',
      'Europe': 'EUR',
      'Asia-Pacific': 'SGD',
      'Middle East': 'AED',
      'Africa': 'ZAR'
    };
    
    return regionDefaults[region] || 'USD';
  },

  // Validate currency configuration
  validateCurrencyConfig(config) {
    const errors = [];
    
    if (!config.primaryCurrency || !this.isValidCurrency(config.primaryCurrency)) {
      errors.push('Invalid primary currency');
    }
    
    if (!config.reportingCurrency || !this.isValidCurrency(config.reportingCurrency)) {
      errors.push('Invalid reporting currency');
    }
    
    if (config.supportedCurrencies && Array.isArray(config.supportedCurrencies)) {
      const invalidCurrencies = config.supportedCurrencies.filter(
        currency => !this.isValidCurrency(currency)
      );
      if (invalidCurrencies.length > 0) {
        errors.push(`Invalid supported currencies: ${invalidCurrencies.join(', ')}`);
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Get exchange rate between two currencies (async - uses real-time rates)
  async getExchangeRate(fromCurrency, toCurrency) {
    if (fromCurrency === toCurrency) return 1.0;
    
    const rates = await this.getExchangeRates();
    const fromRate = rates[fromCurrency];
    const toRate = rates[toCurrency];
    
    if (!fromRate || !toRate) return null;
    
    // Convert from base currency (USD) to target currency
    return toRate / fromRate;
  },

  // Get exchange rate synchronously (uses cached or fallback rates)
  getExchangeRateSync(fromCurrency, toCurrency) {
    if (fromCurrency === toCurrency) return 1.0;
    
    const rates = exchangeRateCache.rates || this.FALLBACK_EXCHANGE_RATES;
    const fromRate = rates[fromCurrency];
    const toRate = rates[toCurrency];
    
    if (!fromRate || !toRate) return null;
    
    // Convert from base currency (USD) to target currency
    return toRate / fromRate;
  },

  // Convert amount using real-time exchange rates (async)
  async convertAmount(amount, fromCurrency, toCurrency) {
    if (fromCurrency === toCurrency) return amount;
    
    const rate = await this.getExchangeRate(fromCurrency, toCurrency);
    if (!rate) return null;
    
    return parseFloat(amount) * rate;
  },

  // Convert amount using cached/fallback exchange rates (synchronous)
  convertAmountSync(amount, fromCurrency, toCurrency) {
    if (fromCurrency === toCurrency) return amount;
    
    const rate = this.getExchangeRateSync(fromCurrency, toCurrency);
    if (!rate) return null;
    
    return parseFloat(amount) * rate;
  },

  // Get all exchange rates (async - uses real-time rates)
  async getAllExchangeRates() {
    return await this.getExchangeRates();
  },

  // Get all exchange rates synchronously (uses cached or fallback)
  getAllExchangeRatesSync() {
    return exchangeRateCache.rates || this.FALLBACK_EXCHANGE_RATES;
  },

  // Get exchange rate for a specific currency (vs USD) - async
  async getExchangeRateToUSD(currency) {
    const rates = await this.getExchangeRates();
    return rates[currency] || null;
  },

  // Get exchange rate for a specific currency (vs USD) - sync
  getExchangeRateToUSDSync(currency) {
    const rates = exchangeRateCache.rates || this.FALLBACK_EXCHANGE_RATES;
    return rates[currency] || null;
  },

  // Clear exchange rate cache (force refresh on next request)
  clearExchangeRateCache() {
    exchangeRateCache.rates = null;
    exchangeRateCache.timestamp = null;
  }
};

export default CurrencyService;
